import { MenuOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Tabs } from "antd";
import { Header } from "antd/es/layout/layout";
import cn from "classnames";
import { ReactNode, useCallback, useState } from "react";
import { FreeMode, Mousewheel, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { zIndexes } from "utils/zIndexes";

import "swiper/css";
import "swiper/css/scrollbar";
import "swiper/css/free-mode";

import { AccountDropdown } from "../Layout/AccountDropdown/AccountDropdown";
import { TenantLink } from "../TenantLink/TenantLink";

import type { Swiper as SwiperType } from "swiper/types";

interface HeaderNavigationProps {
    tabItems: Array<{
        key: string;
        label: JSX.Element;
    }>;
    buttons: ReactNode;
    tenantSlug?: string;
    activeTab?: string;
    className?: string;
    onMobileMenuToggle?: () => void;
    showSettingsLink?: boolean;
    // Only passed by TenantHeaderNavigation — wallet data is tenant-scoped and must never be fetched from account context.
    walletIndicator?: ReactNode;
}

export const HeaderNavigation = ({
    tabItems,
    buttons,
    activeTab,
    tenantSlug,
    className,
    onMobileMenuToggle,
    showSettingsLink = false,
    walletIndicator,
}: HeaderNavigationProps) => {
    const [showRightShadow, setShowRightShadow] = useState(false);

    const handleSwiperUpdate = useCallback((swiper: SwiperType) => {
        const wrapperEl = swiper.wrapperEl;
        const containerEl = swiper.el;
        const isScrollable = wrapperEl.scrollWidth > containerEl.clientWidth;
        const isAtEnd = swiper.isEnd;
        setShowRightShadow(isScrollable && !isAtEnd);
    }, []);

    return (
        <Header
            className={cn(className, `h-[2.875rem] p-0 sticky top-0 z-${zIndexes.headerMenu} border-b border-gray-200`)}
        >
            <div className={"flex h-full items-center justify-between gap-3 lg:gap-6 mx-1 md:mx-10"}>
                <div className="flex-grow min-w-0 relative">
                    <Swiper
                        modules={[Scrollbar, Mousewheel, FreeMode]}
                        slidesPerView="auto"
                        freeMode={true}
                        mousewheel={{ releaseOnEdges: true }}
                        scrollbar={{ hide: true, draggable: true }}
                        className="header-nav-swiper h-full [--swiper-scrollbar-bottom:0px] [--swiper-scrollbar-size:3px] [&_.swiper-wrapper]:items-center [&:hover_.swiper-scrollbar]:!opacity-100"
                        onProgress={handleSwiperUpdate}
                        onResize={handleSwiperUpdate}
                        onInit={handleSwiperUpdate}
                    >
                        {tabItems && (
                            <SwiperSlide className="!w-auto">
                                <Tabs
                                    items={tabItems}
                                    activeKey={activeTab}
                                    tabPosition="top"
                                    className="[&_.ant-tabs-tab]:!text-gray-800 [&_.ant-tabs-tab_a]:!text-gray-800 [&_.ant-tabs-tab.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#008080] [&_.ant-tabs-tab.ant-tabs-tab-active_a]:!text-[#008080]"
                                />
                            </SwiperSlide>
                        )}
                        {buttons && (
                            <SwiperSlide className="!w-auto !ml-auto !flex items-center">{buttons}</SwiperSlide>
                        )}
                    </Swiper>
                    <div
                        className={cn(
                            "absolute right-0 top-0 bottom-0 w-3 pointer-events-none transition-opacity duration-200",
                            "bg-gradient-to-l from-[#e2e2e2] to-transparent",
                            showRightShadow ? "opacity-100" : "opacity-0"
                        )}
                    />
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                    {walletIndicator}
                    {tenantSlug && showSettingsLink && (
                        <TenantLink to="/setting/general" className={"text-black"}>
                            <SettingOutlined />
                        </TenantLink>
                    )}
                    <AccountDropdown />
                    {onMobileMenuToggle && (
                        <Button
                            type="text"
                            icon={<MenuOutlined />}
                            onClick={onMobileMenuToggle}
                            className="lg:hidden text-black !w-auto"
                        />
                    )}
                </div>
            </div>
        </Header>
    );
};
